"use client"

import { api } from "@/lib/api-client"
import { useApiMutation } from "@/shared/hooks/use-api-mutation"
import { useApiQuery } from "@/shared/hooks/use-api-query"
import { useOptimisticMutation } from "@/shared/hooks/use-optimistic-mutation"
import { type ApiDataEnvelope, unwrapApiData } from "@/shared/lib/api-envelope"
import { useQueryClient } from "@tanstack/react-query"

export type TCategory = {
    id: string
    userId: string
    name: string
    color: string
    icon?: string
    isSystem: boolean
    createdAt: string
    updatedAt: string | null
}

export type TCategoryCreate = {
    name: string
    color: string
    icon?: string
}

export type TCategoryUpdate = {
    id: string
    name?: string
    color?: string
    icon?: string
}

const QUERY_KEYS = {
    all: ["categories"] as const,
    byId: (id: string) => ["categories", id] as const,
}

export function useCategoriesQuery() {
    return useApiQuery(QUERY_KEYS.all, async () => {
        const response = await api.categories.$get()
        if (!response.ok) throw new Error("Failed to fetch categories")
        const payload = (await response.json()) as unknown as ApiDataEnvelope<TCategory[]> | TCategory[]
        return unwrapApiData(payload)
    })
}

export function useCategoryQuery(id: string) {
    return useApiQuery(
        QUERY_KEYS.byId(id),
        async () => {
            const response = await api.categories[":id"].$get({ param: { id } })
            if (!response.ok) throw new Error("Category not found")
            const payload = (await response.json()) as unknown as ApiDataEnvelope<TCategory> | TCategory
            return unwrapApiData(payload)
        },
        { enabled: !!id }
    )
}

export function useCreateCategory() {
    const queryClient = useQueryClient()

    return useApiMutation(
        async (data: TCategoryCreate) => {
            const response = await api.categories.$post({ json: data })
            if (!response.ok) throw new Error("Failed to create category")
            const payload = (await response.json()) as unknown as ApiDataEnvelope<TCategory> | TCategory
            return unwrapApiData(payload)
        },
        {
            showSuccessToast: true,
            successMessage: "Category created",
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all })
            },
        }
    )
}

export function useUpdateCategory() {
    return useOptimisticMutation<TCategory, TCategoryUpdate>({
        queryKey: QUERY_KEYS.all,
        mutationFn: async (data) => {
            const response = await api.categories[":id"].$put({
                param: { id: data.id },
                json: {
                    name: data.name,
                    color: data.color,
                    icon: data.icon,
                },
            })
            if (!response.ok) throw new Error("Failed to update category")
            const payload = (await response.json()) as unknown as ApiDataEnvelope<TCategory> | TCategory
            return unwrapApiData(payload)
        },
        optimisticUpdate: (old, data) =>
            (old ?? []).map((c) => (c.id === data.id ? { ...c, ...data } : c)),
        showSuccessToast: true,
        successMessage: "Category updated",
    })
}

export function useDeleteCategory() {
    return useOptimisticMutation<TCategory, string>({
        queryKey: QUERY_KEYS.all,
        mutationFn: async (id) => {
            const response = await api.categories[":id"].$delete({ param: { id } })
            if (!response.ok) throw new Error("Failed to delete category")
            const payload = (await response.json()) as unknown as ApiDataEnvelope<{ deleted: true }> | { deleted: true }
            return unwrapApiData(payload)
        },
        optimisticUpdate: (old, id) => (old ?? []).filter((c) => c.id !== id),
        showSuccessToast: true,
        successMessage: "Category deleted",
    })
}

export function useBulkDeleteCategories() {
    const queryClient = useQueryClient()

    return useApiMutation(
        async (ids: string[]) => {
            await Promise.all(
                ids.map((id) => api.categories[":id"].$delete({ param: { id } }))
            )
            return { success: true }
        },
        {
            showSuccessToast: true,
            successMessage: "Categories deleted",
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all })
            },
        }
    )
}
